/**
 * User-facing support ticket service for trAInedup.ai
 * Handles ticket creation, viewing, and messaging for regular users (non-admin)
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, auth, functions } from './config';

export interface UserTicket {
  id: string;
  ticketNumber: string;
  subject: string;
  description: string;
  category: 'technical' | 'billing' | 'account' | 'course' | 'community' | 'other';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'open' | 'in_progress' | 'waiting_for_user' | 'resolved' | 'closed';
  createdAt: Date;
  updatedAt: Date;
  resolvedAt?: Date;
  satisfactionRating?: number;
  satisfactionFeedback?: string;
  messages: TicketMessage[];
}

export interface TicketMessage {
  id: string;
  senderId: string;
  senderName: string;
  senderType: 'user' | 'agent' | 'system';
  content: string;
  isInternal: boolean;
  createdAt: Date;
}

export interface CreateTicketData {
  subject: string;
  description: string;
  category: 'technical' | 'billing' | 'account' | 'course' | 'community' | 'other';
  priority?: 'low' | 'medium' | 'high';
}

class SupportService {
  /**
   * Get all tickets for the current user
   */
  async getUserTickets(): Promise<UserTicket[]> {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User must be authenticated');
    }

    try {
      const q = query(
        collection(db, 'support_tickets'),
        where('userId', '==', user.uid),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);
      const tickets: UserTicket[] = [];

      for (const docSnap of snapshot.docs) {
        const data = docSnap.data();
        tickets.push({
          id: docSnap.id,
          ticketNumber: data.ticketNumber,
          subject: data.subject,
          description: data.description,
          category: data.category,
          priority: data.priority,
          status: data.status,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
          resolvedAt: data.resolvedAt?.toDate(),
          satisfactionRating: data.satisfactionRating,
          satisfactionFeedback: data.satisfactionFeedback,
          messages: [], // Messages loaded separately
        });
      }

      return tickets;
    } catch (error) {
      console.error('Error fetching user tickets:', error);
      throw error;
    }
  }

  /**
   * Get a single ticket with messages (user can only view their own)
   */
  async getTicket(ticketId: string): Promise<UserTicket | null> {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User must be authenticated');
    }

    try {
      const ticketDoc = await getDoc(doc(db, 'support_tickets', ticketId));
      if (!ticketDoc.exists()) {
        return null;
      }

      const data = ticketDoc.data();

      // Verify the ticket belongs to the user
      if (data.userId !== user.uid) {
        throw new Error('You do not have permission to view this ticket');
      }

      // Get messages (excluding internal notes)
      const messagesQuery = query(
        collection(db, 'support_tickets', ticketId, 'messages'),
        where('isInternal', '==', false),
        orderBy('createdAt', 'asc')
      );
      const messagesSnapshot = await getDocs(messagesQuery);
      const messages: TicketMessage[] = messagesSnapshot.docs.map(msgDoc => ({
        id: msgDoc.id,
        senderId: msgDoc.data().senderId,
        senderName: msgDoc.data().senderName,
        senderType: msgDoc.data().senderType,
        content: msgDoc.data().content,
        isInternal: msgDoc.data().isInternal,
        createdAt: msgDoc.data().createdAt?.toDate(),
      }));

      return {
        id: ticketDoc.id,
        ticketNumber: data.ticketNumber,
        subject: data.subject,
        description: data.description,
        category: data.category,
        priority: data.priority,
        status: data.status,
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate(),
        resolvedAt: data.resolvedAt?.toDate(),
        satisfactionRating: data.satisfactionRating,
        satisfactionFeedback: data.satisfactionFeedback,
        messages,
      };
    } catch (error) {
      console.error('Error fetching ticket:', error);
      throw error;
    }
  }

  /**
   * Create a new support ticket
   */
  async createTicket(ticketData: CreateTicketData): Promise<string> {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User must be authenticated');
    }

    try {
      // Get user info
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const userData = userDoc.data();

      // Generate ticket number
      const ticketNumber = `TKT-${Date.now().toString(36).toUpperCase()}`;

      const ticket = {
        ticketNumber,
        subject: ticketData.subject,
        description: ticketData.description,
        category: ticketData.category,
        priority: ticketData.priority || 'medium',
        status: 'open' as const,
        userId: user.uid,
        userEmail: user.email || '',
        userName: userData?.displayName || user.email || 'User',
        attachments: [],
        tags: [],
        labels: [],
        createdAt: Timestamp.fromDate(new Date()),
        updatedAt: Timestamp.fromDate(new Date()),
      };

      const docRef = await addDoc(collection(db, 'support_tickets'), ticket);

      // Add initial message with the description
      await addDoc(collection(db, 'support_tickets', docRef.id, 'messages'), {
        ticketId: docRef.id,
        senderId: user.uid,
        senderName: userData?.displayName || user.email || 'User',
        senderType: 'user',
        content: ticketData.description,
        isInternal: false,
        attachments: [],
        createdAt: Timestamp.fromDate(new Date()),
        updatedAt: Timestamp.fromDate(new Date()),
      });

      // Send notification email to admins (via Cloud Function)
      try {
        const notifyAdminsFn = httpsCallable(functions, 'notifyNewSupportTicket');
        await notifyAdminsFn({
          ticketId: docRef.id,
          ticketNumber,
          subject: ticketData.subject,
          category: ticketData.category,
          userName: userData?.displayName || user.email,
          userEmail: user.email,
        });
      } catch (emailError) {
        // Don't fail ticket creation if email notification fails
        console.error('Failed to send admin notification:', emailError);
      }

      return docRef.id;
    } catch (error) {
      console.error('Error creating support ticket:', error);
      throw error;
    }
  }

  /**
   * Add a reply message to a ticket (user can only reply to their own)
   */
  async addReply(ticketId: string, content: string): Promise<string> {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User must be authenticated');
    }

    try {
      // Verify the ticket belongs to the user
      const ticketDoc = await getDoc(doc(db, 'support_tickets', ticketId));
      if (!ticketDoc.exists()) {
        throw new Error('Ticket not found');
      }

      const ticketData = ticketDoc.data();
      if (ticketData.userId !== user.uid) {
        throw new Error('You do not have permission to reply to this ticket');
      }

      // Get user info
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const userData = userDoc.data();

      const message = {
        ticketId,
        senderId: user.uid,
        senderName: userData?.displayName || user.email || 'User',
        senderType: 'user' as const,
        content,
        isInternal: false,
        attachments: [],
        createdAt: Timestamp.fromDate(new Date()),
        updatedAt: Timestamp.fromDate(new Date()),
      };

      const messageRef = await addDoc(
        collection(db, 'support_tickets', ticketId, 'messages'),
        message
      );

      // Update ticket's updatedAt and status if it was waiting_for_user
      const ticketUpdate: any = {
        updatedAt: Timestamp.fromDate(new Date()),
      };

      if (ticketData.status === 'waiting_for_user') {
        ticketUpdate.status = 'open';
      }

      await updateDoc(doc(db, 'support_tickets', ticketId), ticketUpdate);

      // Notify admins about new reply (via Cloud Function)
      try {
        const notifyReplyFn = httpsCallable(functions, 'notifyTicketReply');
        await notifyReplyFn({
          ticketId,
          ticketNumber: ticketData.ticketNumber,
          subject: ticketData.subject,
          replyFrom: 'user',
          userName: userData?.displayName || user.email,
        });
      } catch (emailError) {
        console.error('Failed to send reply notification:', emailError);
      }

      return messageRef.id;
    } catch (error) {
      console.error('Error adding reply:', error);
      throw error;
    }
  }

  /**
   * Submit satisfaction rating for a resolved ticket
   */
  async submitSatisfaction(
    ticketId: string,
    rating: number,
    feedback?: string
  ): Promise<void> {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User must be authenticated');
    }

    if (rating < 1 || rating > 5) {
      throw new Error('Rating must be between 1 and 5');
    }

    try {
      // Verify the ticket belongs to the user and is resolved
      const ticketDoc = await getDoc(doc(db, 'support_tickets', ticketId));
      if (!ticketDoc.exists()) {
        throw new Error('Ticket not found');
      }

      const ticketData = ticketDoc.data();
      if (ticketData.userId !== user.uid) {
        throw new Error('You do not have permission to rate this ticket');
      }

      if (ticketData.status !== 'resolved' && ticketData.status !== 'closed') {
        throw new Error('Can only rate resolved or closed tickets');
      }

      await updateDoc(doc(db, 'support_tickets', ticketId), {
        satisfactionRating: rating,
        satisfactionFeedback: feedback || null,
        updatedAt: Timestamp.fromDate(new Date()),
      });
    } catch (error) {
      console.error('Error submitting satisfaction:', error);
      throw error;
    }
  }

  /**
   * Get count of open tickets for the user (for badge/notification)
   */
  async getOpenTicketCount(): Promise<number> {
    const user = auth.currentUser;
    if (!user) {
      return 0;
    }

    try {
      const q = query(
        collection(db, 'support_tickets'),
        where('userId', '==', user.uid),
        where('status', 'in', ['open', 'in_progress', 'waiting_for_user'])
      );

      const snapshot = await getDocs(q);
      return snapshot.size;
    } catch (error) {
      console.error('Error getting open ticket count:', error);
      return 0;
    }
  }
}

export const supportService = new SupportService();
export default supportService;
